<script setup lang="ts">
import type { VideoCallChatDisplayMessage } from "~/types/videoCalls";

const props = defineProps<{
  messages: VideoCallChatDisplayMessage[];
  currentParticipantId: string;
  maxBodyLength: number;
}>();

const emit = defineEmits<{
  close: [];
  retry: [messageId: string];
  send: [body: string];
}>();

const draft = ref("");
const messageList = ref<HTMLElement | null>(null);
const composer = ref<HTMLTextAreaElement | null>(null);
const normalizedDraft = computed(() => draft.value.trim());
const canSend = computed(
  () =>
    normalizedDraft.value.length > 0 &&
    normalizedDraft.value.length <= props.maxBodyLength,
);

const formatMessageTime = (message: VideoCallChatDisplayMessage): string => {
  const value = message.server_sent_at ?? message.client_sent_at;
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const scrollToLatestMessage = async () => {
  await nextTick();
  if (messageList.value) {
    messageList.value.scrollTop = messageList.value.scrollHeight;
  }
};

const submit = () => {
  if (!canSend.value) return;
  emit("send", normalizedDraft.value);
  draft.value = "";
};

const onComposerKeydown = (event: KeyboardEvent) => {
  if (event.key !== "Enter" || event.shiftKey) return;
  event.preventDefault();
  submit();
};

watch(
  () =>
    props.messages
      .map((message) => `${message.message_id}:${message.status}`)
      .join("|"),
  scrollToLatestMessage,
  { immediate: true },
);

onMounted(() => composer.value?.focus());
</script>

<template>
  <aside
    class="video-call-chat"
    aria-label="Chat da chamada"
    data-testid="video-call-chat"
  >
    <header class="video-call-chat__header">
      <div>
        <span>Conversa</span>
        <h3>Chat da chamada</h3>
      </div>
      <button
        type="button"
        class="video-call-chat__close"
        aria-label="Voltar para a chamada"
        @click="emit('close')"
      >
        <i class="mdi mdi-close" aria-hidden="true" />
      </button>
    </header>

    <p class="video-call-chat__notice">
      <i class="mdi mdi-clock-outline" aria-hidden="true" />
      As mensagens ficam disponíveis apenas durante esta chamada.
    </p>

    <div
      ref="messageList"
      class="video-call-chat__messages"
      role="log"
      aria-live="polite"
      aria-relevant="additions text"
    >
      <div v-if="!messages.length" class="video-call-chat__empty">
        <i class="mdi mdi-message-text-outline" aria-hidden="true" />
        <strong>Nenhuma mensagem ainda</strong>
        <span>Envie uma mensagem para quem está na chamada.</span>
      </div>

      <article
        v-for="message in messages"
        :key="`${message.participant.id}:${message.message_id}`"
        class="video-call-chat__message"
        :class="{
          'video-call-chat__message--own':
            message.participant.id === currentParticipantId,
          'video-call-chat__message--failed': message.status === 'failed',
        }"
      >
        <div class="video-call-chat__message-meta">
          <strong>
            {{
              message.participant.id === currentParticipantId
                ? "Você"
                : message.participant.name
            }}
          </strong>
          <span v-if="formatMessageTime(message)">
            {{ formatMessageTime(message) }}
          </span>
        </div>
        <p>{{ message.body }}</p>
        <span
          v-if="message.status === 'pending'"
          class="video-call-chat__delivery"
          role="status"
        >
          Enviando...
        </span>
        <div
          v-else-if="message.status === 'failed'"
          class="video-call-chat__failure"
          role="alert"
        >
          <span>{{ message.error || "Não foi possível enviar." }}</span>
          <button type="button" @click="emit('retry', message.message_id)">
            Reenviar
          </button>
        </div>
      </article>
    </div>

    <form class="video-call-chat__composer" @submit.prevent="submit">
      <label for="video-call-chat-message">Mensagem</label>
      <div class="video-call-chat__composer-row">
        <textarea
          id="video-call-chat-message"
          ref="composer"
          v-model="draft"
          :maxlength="maxBodyLength"
          rows="2"
          placeholder="Escreva uma mensagem"
          @keydown="onComposerKeydown"
        />
        <button type="submit" :disabled="!canSend" aria-label="Enviar mensagem">
          <i class="mdi mdi-send" aria-hidden="true" />
        </button>
      </div>
    </form>
  </aside>
</template>

<style scoped lang="scss">
.video-call-chat {
  display: flex;
  flex: 0 0 340px;
  min-width: 0;
  min-height: 0;
  flex-direction: column;
  overflow: hidden;
  border-left: 1px solid #e3e8f2;
  background: #fff;
}

.video-call-chat__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px;
  border-bottom: 1px solid #edf0f6;

  div {
    display: grid;
    gap: 2px;
  }

  span {
    color: #69758b;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  h3 {
    margin: 0;
    color: #17233c;
    font-size: 1rem;
  }
}

.video-call-chat__close {
  display: inline-flex;
  width: 36px;
  height: 36px;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 10px;
  background: #f0f3f9;
  color: #273550;
  cursor: pointer;
}

.video-call-chat__notice {
  display: flex;
  gap: 8px;
  padding: 10px 12px;
  margin: 12px;
  border-radius: 10px;
  background: #eef2ff;
  color: #40517a;
  font-size: 0.76rem;
  line-height: 1.35;
}

.video-call-chat__messages {
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
  gap: 10px;
  padding: 4px 12px 12px;
  overflow-y: auto;
}

.video-call-chat__empty {
  display: grid;
  flex: 1;
  place-content: center;
  justify-items: center;
  padding: 24px;
  color: #69758b;
  text-align: center;

  i {
    margin-bottom: 8px;
    color: #8290aa;
    font-size: 1.8rem;
  }

  strong {
    color: #34415c;
    font-size: 0.84rem;
  }

  span {
    margin-top: 3px;
    font-size: 0.76rem;
  }
}

.video-call-chat__message {
  align-self: flex-start;
  max-width: 88%;
  padding: 9px 11px;
  border-radius: 4px 13px 13px;
  background: #f0f3f9;
  color: #273550;

  p {
    margin: 3px 0 0;
    font-size: 0.82rem;
    line-height: 1.4;
    overflow-wrap: anywhere;
    white-space: pre-wrap;
  }
}

.video-call-chat__message--own {
  align-self: flex-end;
  border-radius: 13px 4px 13px 13px;
  background: #e7ecff;
}

.video-call-chat__message--failed {
  border: 1px solid #f2b8bf;
  background: #fff1f2;
}

.video-call-chat__message-meta {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;

  strong {
    color: #1d2a44;
    font-size: 0.72rem;
  }

  span {
    color: #77839a;
    font-size: 0.66rem;
  }
}

.video-call-chat__delivery {
  display: block;
  margin-top: 4px;
  color: #65718a;
  font-size: 0.68rem;
}

.video-call-chat__failure {
  display: grid;
  gap: 4px;
  margin-top: 5px;
  color: #9f1b32;
  font-size: 0.68rem;

  button {
    width: fit-content;
    padding: 0;
    border: 0;
    background: transparent;
    color: #9f1b32;
    cursor: pointer;
    font: inherit;
    font-weight: 800;
    text-decoration: underline;
  }
}

.video-call-chat__composer {
  display: grid;
  gap: 6px;
  padding: 12px;
  border-top: 1px solid #e3e8f2;

  label {
    color: #34415c;
    font-size: 0.74rem;
    font-weight: 700;
  }

  button {
    display: inline-flex;
    flex: 0 0 42px;
    width: 42px;
    height: 42px;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 10px;
    background: #3156ff;
    color: #fff;
    cursor: pointer;

    &:disabled {
      cursor: default;
      opacity: 0.45;
    }
  }
}

.video-call-chat__composer-row {
  display: flex;
  align-items: flex-end;
  gap: 8px;

  textarea {
    flex: 1;
    min-width: 0;
    min-height: 64px;
    resize: none;
    border: 1px solid #cfd7e6;
    border-radius: 11px;
    outline: none;
    padding: 9px 10px;
    color: #24314b;
    font: inherit;
    font-size: 0.82rem;

    &:focus {
      border-color: #3156ff;
      box-shadow: 0 0 0 3px rgba(49, 86, 255, 0.12);
    }
  }
}

@media (max-width: 600px) {
  .video-call-chat {
    position: absolute;
    z-index: 10;
    inset: 0;
    border: 0;
  }

  .video-call-chat__messages {
    min-height: 0;
  }
}
</style>
